import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, 
  Send, 
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  CheckCircle,
  Brain,
  Heart,
  Activity
} from 'lucide-react';
import { useClientIsolation } from '../../hooks/useClientIsolation';

interface SomaChatMessage {
  id: string;
  role: 'user' | 'soma';
  message: string;
  timestamp: Date;
  tone: 'encouraging' | 'motivational' | 'supportive' | 'educational' | 'celebratory' | 'medical';
  suggestions?: string[];
  followUpQuestions?: string[];
  medicalDisclaimer?: string;
  actions?: {
    type: string;
    details: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    requiresMedicalReview?: boolean;
  };
  privacy: {
    dataClassification: 'public' | 'internal' | 'confidential' | 'restricted';
    encryptionApplied: boolean;
    auditLogged: boolean;
  };
  somaIntegration?: {
    diagnosis?: {
      confidence: number;
      explanation: string;
      recommendations: string[];
    };
    goalAlignment?: {
      currentGoal: string;
      progress: number;
      nextSteps: string[];
    };
  };
}

interface SomaChatPanelProps {
  userId: string;
  sessionId: string;
  clientId?: string;
  tenantId?: string;
  userProfile: any;
  medicalData?: any;
  privacySettings: any;
  onMessageSent?: (message: SomaChatMessage) => void;
  onActionTriggered?: (action: any) => void;
  demoMode?: boolean;
}

export const SomaChatPanel: React.FC<SomaChatPanelProps> = ({
  userId,
  sessionId,
  clientId,
  tenantId,
  userProfile,
  medicalData,
  privacySettings,
  onMessageSent,
  onActionTriggered,
  demoMode = false
}) => {
  // CLIENT ISOLATION: Use client isolation hook
  const { clientContext, createIsolatedRequest, validateAccess } = useClientIsolation();
  const [messages, setMessages] = useState<SomaChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(false);
  const [messageType, setMessageType] = useState<'text' | 'voice' | 'image' | 'workout_data' | 'medical_question'>('text');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const demoConversation: SomaChatMessage[] = [
    {
      id: 'demo_1',
      role: 'soma',
      message: "Hi Toshika! I'm SOMA, your AI wellness coach. I see you're interested in improving your nutrition. What are your main health goals right now?",
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
      tone: 'supportive',
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: true,
        auditLogged: true
      }
    },
    {
      id: 'demo_2',
      role: 'user',
      message: "Hi SOMA! I want to lose about 15 pounds and have more energy throughout the day. I've been feeling sluggish lately.",
      timestamp: new Date(Date.now() - 240000), // 4 minutes ago
      tone: 'encouraging',
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: true,
        auditLogged: true
      }
    },
    {
      id: 'demo_3',
      role: 'soma',
      message: "That's a great goal, Toshika! Weight loss and energy are closely connected. Let's start with your current eating patterns. Can you tell me what a typical day of meals looks like for you?",
      timestamp: new Date(Date.now() - 180000), // 3 minutes ago
      tone: 'educational',
      suggestions: [
        "Track your meals for 3 days to identify patterns",
        "Focus on protein at each meal for sustained energy",
        "Consider meal timing - eating every 3-4 hours"
      ],
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: true,
        auditLogged: true
      }
    },
    {
      id: 'demo_4',
      role: 'user',
      message: "I usually skip breakfast, have a sandwich for lunch, and then a big dinner. I snack a lot in the evening while watching TV.",
      timestamp: new Date(Date.now() - 120000), // 2 minutes ago
      tone: 'encouraging',
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: true,
        auditLogged: true
      }
    },
    {
      id: 'demo_5',
      role: 'soma',
      message: "I see the pattern! Skipping breakfast often leads to energy crashes and evening overeating. Let's create a balanced meal plan that will stabilize your blood sugar and boost your energy.",
      timestamp: new Date(Date.now() - 60000), // 1 minute ago
      tone: 'motivational',
      suggestions: [
        "Start with a protein-rich breakfast (eggs, Greek yogurt, or protein smoothie)",
        "Add healthy fats and fiber to your lunch",
        "Plan satisfying dinners to reduce evening snacking",
        "Try herbal tea instead of snacks while watching TV"
      ],
      followUpQuestions: [
        "What foods do you enjoy eating?",
        "Do you have any dietary restrictions?",
        "How much time can you dedicate to meal prep?"
      ],
      actions: {
        type: 'create_meal_plan',
        details: 'Generate personalized meal plan based on Toshika\'s preferences and goals',
        priority: 'medium'
      },
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: true,
        auditLogged: true
      },
      somaIntegration: {
        goalAlignment: {
          currentGoal: 'Weight loss and increased energy',
          progress: 0,
          nextSteps: [
            'Establish consistent meal timing',
            'Increase protein intake',
            'Reduce evening snacking'
          ]
        }
      }
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (demoMode && messages.length === 0) {
      setMessages(demoConversation);
    }
  }, [demoMode]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: SomaChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      message: inputMessage,
      timestamp: new Date(),
      tone: 'encouraging',
      privacy: {
        dataClassification: 'internal',
        encryptionApplied: privacySettings.encryptionLevel !== 'standard',
        auditLogged: true
      }
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    if (demoMode) {
      setTimeout(() => {
        const demoResponses = [
          "That's a great question! In demo mode, I can help you explore nutrition topics. Try asking about meal planning, macronutrients, or healthy recipes.",
          "I'd love to help you with that! For a full personalized response, you'll want to connect with the live SOMA system. In demo mode, I can share general wellness tips.",
          "Excellent point! Nutrition is so important for overall health. Would you like me to share some evidence-based tips about balanced eating?",
          "That's something I can definitely help with! Proper nutrition supports both physical and mental wellbeing. What specific area interests you most?"
        ];
        
        const randomResponse = demoResponses[Math.floor(Math.random() * demoResponses.length)];
        
        const somaMessage: SomaChatMessage = {
          id: `soma_demo_${Date.now()}`,
          role: 'soma',
          message: randomResponse,
          timestamp: new Date(),
          tone: 'supportive',
          suggestions: [
            "Ask about meal planning strategies",
            "Learn about macronutrient balance",
            "Explore healthy recipe ideas",
            "Discuss portion control techniques"
          ],
          privacy: {
            dataClassification: 'internal',
            encryptionApplied: true,
            auditLogged: true
          }
        };

        setMessages(prev => [...prev, somaMessage]);
        setIsLoading(false);
        
        if (onMessageSent) {
          onMessageSent(somaMessage);
        }
      }, 1500); // Simulate thinking time
      return;
    }

    try {
      // CLIENT ISOLATION: Validate access and create isolated request
      if (!validateAccess('sculptd_fitness_coaching')) {
        throw new Error('Access denied: Client does not have permission for fitness coaching');
      }

      const response = await fetch('/api/support-chat', 
        createIsolatedRequest('/api/support-chat', {
          method: 'POST',
          body: JSON.stringify({
            sessionId,
            message: inputMessage,
            attachments: [],
            context: {
              userId,
              clientId: clientContext?.clientId,
              tenantId: clientContext?.tenantId,
              userProfile,
              currentProgram: userProfile?.currentProgram,
              recentProgress: userProfile?.recentProgress,
              mood: userProfile?.mood || 'neutral',
              privacyLevel: privacySettings.encryptionLevel,
              medicalData,
              source: 'sculptd_chat_panel'
            }
          })
        })
      );

      if (!response.ok) {
        throw new Error(`SOMA chat request failed: ${response.status}`);
      }

      const somaResponse = await response.json();
      
      const somaMessage: SomaChatMessage = {
        id: `soma_${Date.now()}`,
        role: 'soma',
        message: somaResponse.response.message,
        timestamp: new Date(),
        tone: somaResponse.response.tone,
        suggestions: somaResponse.response.suggestions,
        followUpQuestions: somaResponse.response.followUpQuestions,
        medicalDisclaimer: somaResponse.response.medicalDisclaimer,
        actions: somaResponse.actions,
        privacy: somaResponse.privacy,
        somaIntegration: somaResponse.somaIntegration
      };

      setMessages(prev => [...prev, somaMessage]);
      
      if (onMessageSent) {
        onMessageSent(somaMessage);
      }

      if (somaResponse.actions && onActionTriggered) {
        onActionTriggered(somaResponse.actions);
      }

    } catch (error) {
      console.error('Error sending message to SOMA:', error);
      const errorMessage: SomaChatMessage = {
        id: `error_${Date.now()}`,
        role: 'soma',
        message: 'I apologize, but I\'m having trouble processing your request right now. Please try again in a moment.',
        timestamp: new Date(),
        tone: 'supportive',
        privacy: {
          dataClassification: 'internal',
          encryptionApplied: false,
          auditLogged: true
        }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getToneIcon = (tone: string) => {
    switch (tone) {
      case 'celebratory': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'medical': return <Heart className="w-4 h-4 text-red-500" />;
      case 'motivational': return <Activity className="w-4 h-4 text-blue-500" />;
      case 'educational': return <Brain className="w-4 h-4 text-purple-500" />;
      default: return <MessageCircle className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />;
    }
  };

  const getPrivacyIcon = (classification: string) => {
    switch (classification) {
      case 'restricted': return <Lock className="w-4 h-4 text-red-500" />;
      case 'confidential': return <Shield className="w-4 h-4 text-orange-500" />;
      case 'internal': return <Eye className="w-4 h-4 text-yellow-500" />;
      default: return <EyeOff className="w-4 h-4 text-green-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      default: return 'style={{ borderColor: "var(--color-border)", background: "var(--color-background)" }}';
    }
  };

  return (
    <div className="flex flex-col h-full sculptd-card-elevated sculptd-sheen" style={{
      boxShadow: 'var(--shadow-card-separation)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-between sculptd-container-padding border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-1">
          {getPrivacyIcon(privacySettings.encryptionLevel === 'enhanced' ? 'confidential' : 'internal')}
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {privacySettings.encryptionLevel === 'enhanced' ? 'Enhanced Privacy' : 'Standard Privacy'}
          </span>
        </div>
        <button
          onClick={() => setShowPrivacyInfo(!showPrivacyInfo)}
          className="p-2 transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
        >
          <Shield className="w-5 h-5" />
        </button>
      </div>

      {/* Privacy Info Panel */}
      {showPrivacyInfo && (
        <div className="sculptd-container-padding border-b" style={{ background: 'var(--gradient-cta-primary)', borderColor: 'var(--color-primary)', boxShadow: 'var(--shadow-lg)' }}>
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-primary)' }} />
            <div className="flex-1">
              <h3 className="font-medium" style={{ color: 'var(--color-text)' }}>Privacy & Security</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                Your conversations are protected with {privacySettings.encryptionLevel} encryption. 
                Medical data is classified as restricted and requires additional authorization.
              </p>
              <div className="flex items-center space-x-4 mt-2 text-xs" style={{ color: 'var(--color-primary)' }}>
                <span>Data Retention: {privacySettings.dataRetention} days</span>
                <span>Audit Logging: {privacySettings.auditLogging ? 'Enabled' : 'Disabled'}</span>
                <span>Medical Sharing: {privacySettings.allowMedicalSharing ? 'Allowed' : 'Restricted'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto sculptd-container-padding space-y-4">
        {messages.length === 0 && !demoMode && (
          <div className="text-center mt-8" style={{ color: 'var(--color-text-muted)' }}>
            <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)', opacity: 0.5 }} />
            <p className="text-lg font-medium">Welcome to SOMA Coach</p>
            <p className="text-sm mt-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              I'm your AI fitness coach with medical analysis capabilities. 
              Ask me about workouts, nutrition, or any health-related questions.
            </p>
          </div>
        )}

        {demoMode && messages.length > 0 && (
          <div className="mb-4 p-3 rounded-lg" style={{ 
            background: 'rgba(58, 175, 169, 0.1)', 
            border: '1px solid rgba(58, 175, 169, 0.2)' 
          }}>
            <div className="flex items-center space-x-2">
              <Brain className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--color-primary)' }}>
                Demo Mode: Toshika & SOMA Nutrition Conversation
              </span>
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              This is a sample conversation showing SOMA's nutrition coaching capabilities.
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.role === 'user'
                  ? 'text-white'
                  : 'text-[var(--color-text)]'
              }}
              style={message.role === 'soma' ? { background: 'var(--color-background-secondary)' } : { background: 'var(--gradient-cta-primary)', boxShadow: 'var(--shadow-lg)' }}
              }`}
            >
              <div className="flex items-center space-x-3 mb-1">
                {message.role === 'soma' && getToneIcon(message.tone)}
                <span className="text-xs opacity-75">
                  {message.timestamp.toLocaleTimeString()}
                </span>
                {message.privacy.encryptionApplied && (
                  <Lock className="w-3 h-3 opacity-75" />
                )}
              </div>
              
              <p className="text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{message.message}</p>
              
              {message.medicalDisclaimer && (
                <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {message.medicalDisclaimer}
                </div>
              )}
              
              {message.suggestions && message.suggestions.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium mb-1">Suggestions:</p>
                  <ul className="text-xs space-y-1">
                    {message.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-1">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {message.actions && (
                <div className={`mt-2 p-2 border rounded ${getPriorityColor(message.actions.priority)}`}>
                  <p className="text-xs font-medium">
                    Action: {message.actions.type.replace('_', ' ').toUpperCase()}
                  </p>
                  <p className="text-xs mt-1">{message.actions.details}</p>
                  {message.actions.requiresMedicalReview && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      Medical review recommended
                    </p>
                  )}
                </div>
              )}
              
              {message.somaIntegration?.diagnosis && (
                <div className="mt-2 p-2 rounded" style={{ 
                  background: 'rgba(156, 106, 222, 0.1)', 
                  border: '1px solid rgba(156, 106, 222, 0.2)' 
                }}>
                  <p className="text-xs font-medium" style={{ color: 'var(--color-secondary)' }}>SOMA Analysis</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-secondary)', opacity: 0.8 }}>
                    Confidence: {(message.somaIntegration.diagnosis.confidence * 100).toFixed(1)}%
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-secondary)', opacity: 0.8 }}>
                    {message.somaIntegration.diagnosis.explanation}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="px-4 py-2 rounded-lg" style={{ background: 'var(--color-background-secondary)', color: 'var(--color-text)' }}>
              <div className="flex items-center space-x-3">
                <Brain className="w-4 h-4 animate-pulse" />
                <span className="text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>SOMA is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Type Selector */}
      <div className="px-4 py-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-3 mb-2">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Message Type:</span>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value as any)}
            className="text-xs border rounded px-2 py-1"
            style={{ 
              borderColor: 'var(--color-border)', 
              background: 'white',
              color: 'var(--color-text)'
            }}
          >
            <option value="text">Text</option>
            <option value="workout_data">Workout Data</option>
            <option value="medical_question">Medical Question</option>
            <option value="voice">Voice</option>
            <option value="image">Image</option>
          </select>
        </div>
      </div>

      {/* Input */}
      <div className="sculptd-container-padding border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center space-x-3">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask SOMA about your fitness journey..."
            className="flex-1 resize-none border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ 
              borderColor: 'var(--color-border)', 
              background: 'white',
              color: 'var(--color-text)',
              textShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border)';
              e.target.style.boxShadow = 'none';
            }}
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="p-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors sculptd-cta-button"
            style={{ 
              background: 'var(--gradient-cta-primary)',
              boxShadow: 'var(--shadow-lg)'
            }}
            onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.boxShadow = 'var(--shadow-xl)')}
            onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.boxShadow = 'var(--shadow-lg)')}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SomaChatPanel;
